import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { NavBar } from '@/components/NavBar';
import { Footer } from '@/components/Footer';
import { CTASection } from '@/components/CTASection';
import { ExploreMoreSection } from '@/components/ExploreMoreSection';
import { LeadForm } from '@/components/LeadForm';
import { MeetingModal } from '@/components/MeetingModal';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { StickyMobileCTA } from '@/components/StickyMobileCTA';
import { ResponsiveHeroImage } from '@/components/ResponsiveHeroImage';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Store, 
  Calendar, 
  MapPin, 
  Users, 
  UtensilsCrossed, 
  TrendingUp, 
  Phone, 
  Mail,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

const vendorCategories = [
  { name: 'Asian Fusion', status: 'In Discussion' },
  { name: 'Caribbean BBQ', status: 'In Discussion' },
  { name: 'Fresh Juice Bar', status: 'Available' },
  { name: 'Artisan Coffee', status: 'Available' },
  { name: 'Mediterranean', status: 'Available' },
  { name: 'Dessert & Bakery', status: 'Available' },
];

const eventSpaceFeatures = [
  {
    title: 'Flexible Layout',
    description: 'Configurable space to accommodate various event formats and sizes',
  },
  {
    title: 'Full Catering Kitchen',
    description: 'Access to professional kitchen facilities for your event catering needs',
  },
  {
    title: 'Outdoor Terrace',
    description: 'Beautiful outdoor space with lagoon views for receptions and gatherings',
  },
  {
    title: 'A/V Equipment',
    description: 'Built-in audio-visual systems for presentations and entertainment',
  },
];

const locationBenefits = [
  {
    icon: MapPin,
    title: 'Prime Grand Harbour Location',
    description: 'Strategically positioned in the heart of Grand Harbour with excellent visibility and accessibility',
  },
  {
    icon: Users,
    title: 'Built-in Foot Traffic',
    description: 'Access to residents, local community, and visitors to the area',
  },
  {
    icon: TrendingUp,
    title: 'Growing Community',
    description: 'Be part of a rapidly developing neighborhood with increasing demand',
  },
];

export const Commercial = () => {
  const navigate = useNavigate();
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);

  const whatsappNumber = '13451234567';
  const whatsappMessage = encodeURIComponent(
    'Hi, I\'m interested in commercial leasing opportunities at The Pearl at Grand Harbour. Can you provide more information?'
  );

  const handleLeasingInquiry = () => {
    trackEvent('cta_click', { location: 'commercial_page', action: 'leasing_inquiry' });
    setLeadFormOpen(true);
  };

  const handleEventInquiry = () => {
    trackEvent('cta_click', { location: 'commercial_page', action: 'event_inquiry' });
    setMeetingOpen(true);
  };

  const handleWhatsAppClick = () => {
    trackEvent('whatsapp_click', { location: 'commercial_page' });
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Commercial Leasing - The Pearl at Grand Harbour | Food Hall & Event Space</title>
        <meta 
          name="description" 
          content="Lease commercial space at The Pearl at Grand Harbour. Food hall vendor opportunities and flexible event space rental in prime Grand Harbour location." 
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <NavBar 
          onRegisterClick={() => setLeadFormOpen(true)} 
          onConsultClick={() => setMeetingOpen(true)}
        />

        {/* Hero Section */}
        <section className="relative h-[70vh] overflow-hidden pt-20">
          <div className="absolute inset-0">
            <ResponsiveHeroImage
              baseName="food-hall"
              originalSrc="/img/food-hall.jpg"
              alt="The Pearl Commercial Space - Food Hall and Event Venue"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          </div>

          <div className="relative z-10 h-full flex items-end">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
              <div className="max-w-4xl">
                <h1 className="font-display text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Commercial Leasing Opportunities
                </h1>
                <p className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed">
                  Join The Pearl's vibrant commercial community. Food hall vendor spaces and flexible event venues in the heart of Grand Harbour.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-brand-teal hover:bg-brand-navy text-white"
                    onClick={handleLeasingInquiry}
                  >
                    <Store className="w-5 h-5 mr-2" />
                    Explore Leasing Options
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white text-white bg-black/30 hover:bg-white hover:!text-foreground hover:!border-foreground"
                    onClick={handleEventInquiry}
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Inquire About Event Space
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Food Hall Leasing Section */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Store className="w-8 h-8 text-brand-teal" />
                  <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground">
                    Food Hall Leasing
                  </h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Be part of Grand Harbour's premier food destination. Eight curated kitchen concepts in a modern, high-traffic location.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Vendor Opportunities */}
                <div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-6">
                    Available Vendor Spaces
                  </h3>
                  
                  <div className="grid gap-4 mb-8">
                    {vendorCategories.map((vendor) => (
                      <Card 
                        key={vendor.name}
                        className="hover-lift transition-all duration-300 hover:shadow-lg"
                      >
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <UtensilsCrossed className="w-5 h-5 text-brand-teal" />
                              <span className="font-medium text-foreground">{vendor.name}</span>
                            </div>
                            <Badge
                              variant={vendor.status === 'Available' ? 'default' : 'secondary'}
                              className={
                                vendor.status === 'Available'
                                  ? 'bg-brand-teal/20 text-brand-teal border-brand-teal'
                                  : ''
                              }
                            >
                              {vendor.status}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Card className="bg-gradient-to-br from-deep-navy to-midnight-blue text-white border-none">
                    <CardContent className="p-6">
                      <h4 className="font-display text-xl font-semibold mb-3">
                        Why Lease at The Pearl?
                      </h4>
                      <ul className="space-y-3 mb-6">
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-brand-teal" />
                          <span className="text-white/90 text-sm">Flexible lease terms tailored to your business</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-brand-teal" />
                          <span className="text-white/90 text-sm">Built-in foot traffic from residents and community</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-brand-teal" />
                          <span className="text-white/90 text-sm">Modern, fully-equipped kitchen spaces</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-brand-teal" />
                          <span className="text-white/90 text-sm">Marketing support and community integration</span>
                        </li>
                      </ul>
                      <Button
                        variant="outline"
                        className="w-full border-white bg-transparent text-white hover:bg-white/10 hover:text-white"
                        onClick={handleLeasingInquiry}
                      >
                        Request Leasing Information
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Food Hall Overview */}
                <div>
                  <div className="rounded-2xl overflow-hidden shadow-luxury mb-6">
                    <img
                      src="/img/food-hall.jpg"
                      alt="The Pearl Food Hall"
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-display text-2xl font-semibold text-foreground">
                      The Pearl Food Hall
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Our indoor food hall features eight distinct kitchen concepts, each designed to showcase 
                      unique culinary offerings. From morning coffee to evening dining, the food hall serves as 
                      the neighborhood's social hub.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      The space includes a modern outdoor bar and flexible event area, creating opportunities 
                      for community gatherings, private events, and regular programming that drives consistent 
                      foot traffic throughout the week.
                    </p>
                    <div className="pt-4 border-t">
                      <p className="font-semibold text-foreground mb-2">Ideal for:</p>
                      <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-brand-teal" />
                          Established restaurant concepts expanding to Grand Harbour
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-brand-teal" />
                          Emerging food entrepreneurs seeking prime location
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-brand-teal" />
                          Specialty food and beverage concepts
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Event Space Rental Section */}
        <section className="py-16 lg:py-24 bg-soft-cream">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Calendar className="w-8 h-8 text-brand-teal" />
                  <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground">
                    Event Space Rental
                  </h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Host memorable events in our flexible, modern event spaces. Perfect for corporate functions, 
                  private celebrations, and community gatherings.
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-start">
                {/* Left Column - Event Features */}
                <div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-6">
                    Event Space Features
                  </h3>
                  
                  <div className="grid gap-4 mb-8">
                    {eventSpaceFeatures.map((feature) => (
                      <Card key={feature.title} className="hover-lift">
                        <CardContent className="p-5">
                          <h4 className="font-semibold text-lg text-foreground mb-2">
                            {feature.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {feature.description}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Card className="bg-accent/10 border-accent/20">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <Users className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-display text-lg font-semibold text-foreground mb-2">
                            Capacity & Amenities
                          </h4>
                          <ul className="space-y-2 text-sm text-muted-foreground">
                            <li>• Capacity up to 150 guests</li>
                            <li>• Indoor and outdoor event options</li>
                            <li>• Professional catering kitchen access</li>
                            <li>• Built-in A/V systems</li>
                            <li>• Flexible layout configurations</li>
                            <li>• Lagoon views and outdoor terrace</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Use Cases */}
                <div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-6">
                    Perfect For
                  </h3>
                  
                  <div className="space-y-5 mb-8">
                    <Card className="hover-lift">
                      <CardContent className="p-6">
                        <h4 className="font-display text-lg font-semibold text-foreground mb-2">
                          Corporate Events
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Host team meetings, product launches, client dinners, and corporate celebrations in a 
                          professional yet welcoming environment.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="hover-lift">
                      <CardContent className="p-6">
                        <h4 className="font-display text-lg font-semibold text-foreground mb-2">
                          Private Celebrations
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Birthdays, anniversaries, graduations, and family gatherings. Our flexible space adapts 
                          to your celebration style.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="hover-lift">
                      <CardContent className="p-6">
                        <h4 className="font-display text-lg font-semibold text-foreground mb-2">
                          Community Events
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Weekly farmers markets, community workshops, live music nights, and neighborhood 
                          gatherings that bring people together.
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <Button
                    size="lg"
                    className="w-full bg-brand-teal hover:bg-brand-navy text-white"
                    onClick={handleEventInquiry}
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Inquire About Event Space
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Location Benefits Section */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground mb-6">
                  Why The Pearl Location?
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Strategic positioning in Grand Harbour offers unparalleled advantages for commercial tenants
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {locationBenefits.map((benefit) => {
                  const Icon = benefit.icon;
                  return (
                    <Card key={benefit.title} className="hover-lift text-center">
                      <CardContent className="pt-8 pb-6">
                        <div className="w-16 h-16 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon className="w-8 h-8 text-brand-teal" />
                        </div>
                        <h3 className="font-semibold text-xl text-foreground mb-3">
                          {benefit.title}
                        </h3>
                        <p className="text-muted-foreground text-sm">
                          {benefit.description}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Leasing Process Section */}
        <section className="py-16 lg:py-24 bg-soft-cream">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground mb-6">
                  Get Started
                </h2>
                <p className="text-lg text-muted-foreground">
                  Ready to join The Pearl's commercial community? Here's how to begin
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card className="text-center">
                  <CardContent className="pt-8 pb-6">
                    <div className="w-12 h-12 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-brand-teal">1</span>
                    </div>
                    <h3 className="font-semibold text-lg text-foreground mb-2">Inquire</h3>
                    <p className="text-sm text-muted-foreground">
                      Contact us to discuss your commercial space needs
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center">
                  <CardContent className="pt-8 pb-6">
                    <div className="w-12 h-12 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-brand-teal">2</span>
                    </div>
                    <h3 className="font-semibold text-lg text-foreground mb-2">Tour</h3>
                    <p className="text-sm text-muted-foreground">
                      Schedule a site visit to see the spaces in person
                    </p>
                  </CardContent>
                </Card>

                <Card className="text-center">
                  <CardContent className="pt-8 pb-6">
                    <div className="w-12 h-12 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-brand-teal">3</span>
                    </div>
                    <h3 className="font-semibold text-lg text-foreground mb-2">Lease</h3>
                    <p className="text-sm text-muted-foreground">
                      Review terms and finalize your commercial lease agreement
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-white rounded-lg p-8 shadow-soft text-center">
                <h3 className="font-display text-2xl font-semibold text-foreground mb-4">
                  Ready to Learn More?
                </h3>
                <p className="text-muted-foreground mb-6">
                  Contact our commercial leasing team to discuss opportunities, pricing, and availability
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-brand-teal hover:bg-brand-navy text-white"
                    onClick={handleLeasingInquiry}
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Contact Leasing Team
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => {
                      trackEvent('cta_click', { location: 'commercial_page', action: 'email' });
                      window.location.href = 'mailto:info@thepearl.ky?subject=Commercial Leasing Inquiry';
                    }}
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Email Us
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Explore More Section */}
        <ExploreMoreSection 
          onContactClick={() => setLeadFormOpen(true)}
          currentPage="commercial"
        />

        {/* CTA Section */}
        <CTASection 
          onRegisterClick={() => setLeadFormOpen(true)}
          onConsultClick={() => setMeetingOpen(true)}
          onWhatsAppClick={handleWhatsAppClick}
        />

        {/* Footer */}
        <Footer />

        {/* Persistent Conversion UI */}
        <StickyMobileCTA onClick={() => setLeadFormOpen(true)} />
        <WhatsAppButton onClick={handleWhatsAppClick} />

        {/* Modals */}
        <LeadForm open={leadFormOpen} onOpenChange={setLeadFormOpen} />
        <MeetingModal
          open={meetingOpen}
          onOpenChange={setMeetingOpen}
          onWhatsAppClick={handleWhatsAppClick}
        />
      </div>
    </>
  );
};

